import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import {
  Key, Loader2, Plus, Trash2, Copy, Check, Eye, EyeOff, AlertTriangle
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import api from '../../services/api';

const SCOPE_LABELS = {
  blog: 'Blog',
};

const ScopedApiKeysTab = ({ colors }) => {
  const [keys, setKeys] = useState([]);
  const [availableScopes, setAvailableScopes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [newKeyRevealed, setNewKeyRevealed] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revoking, setRevoking] = useState(null);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDescription, setFormDescription] = useState('');
  const [formScopes, setFormScopes] = useState([]);

  const loadKeys = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/api/admin/api-keys');
      setKeys(res.data.keys || []);
      setAvailableScopes(res.data.available_scopes || []);
    } catch (err) {
      toast.error('Failed to load API keys');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  const handleCreate = async () => {
    if (!formName.trim()) {
      toast.error('Key name is required');
      return;
    }
    if (formScopes.length === 0) {
      toast.error('Select at least one scope');
      return;
    }

    try {
      setCreating(true);
      const res = await api.post('/api/admin/api-keys', {
        name: formName.trim(),
        description: formDescription.trim() || null,
        scopes: formScopes,
      });
      setNewKeyRevealed(res.data.key);
      setShowForm(false);
      setFormName('');
      setFormDescription('');
      setFormScopes([]);
      toast.success('API key created');
      loadKeys();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create key');
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (keyId, keyName) => {
    if (!window.confirm(`Revoke key "${keyName}"? This cannot be undone.`)) return;

    try {
      setRevoking(keyId);
      await api.delete(`/api/admin/api-keys/${keyId}`);
      toast.success('Key revoked');
      loadKeys();
    } catch (err) {
      toast.error('Failed to revoke key');
    } finally {
      setRevoking(null);
    }
  };

  const copyKey = async (key) => {
    await navigator.clipboard.writeText(key);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const toggleScope = (scope) => {
    setFormScopes(prev =>
      prev.includes(scope) ? prev.filter(s => s !== scope) : [...prev, scope]
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className={cn("w-8 h-8 animate-spin", colors.textMuted)} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Newly created key banner */}
      {newKeyRevealed && (
        <Card className={cn("border-yellow-500/50", colors.cardBg)}>
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
              <div className="flex-1 space-y-2">
                <p className={cn("text-sm font-medium", colors.textPrimary)}>
                  Copy your API key now — it will not be shown again.
                </p>
                <div className="flex items-center gap-2">
                  <code className={cn(
                    "flex-1 text-sm px-3 py-2 rounded-md font-mono",
                    colors.bgSecondary, colors.textPrimary
                  )}>
                    {newKeyRevealed}
                  </code>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => copyKey(newKeyRevealed)}
                    className="flex-shrink-0"
                  >
                    {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setNewKeyRevealed(null)}
                  className={cn("text-xs", colors.textMuted)}
                >
                  Dismiss
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create key form */}
      {showForm ? (
        <Card className={cn(colors.cardBg, colors.cardBorder)}>
          <CardHeader>
            <CardTitle className={cn("text-lg", colors.textPrimary)}>Create API Key</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className={cn("text-sm font-medium mb-1 block", colors.textSecondary)}>
                Name
              </label>
              <Input
                value={formName}
                onChange={e => setFormName(e.target.value)}
                placeholder="e.g., Blog CI/CD, AI Content Writer"
                className={cn(colors.inputBg, colors.inputBorder)}
              />
            </div>
            <div>
              <label className={cn("text-sm font-medium mb-1 block", colors.textSecondary)}>
                Description (optional)
              </label>
              <Input
                value={formDescription}
                onChange={e => setFormDescription(e.target.value)}
                placeholder="What this key is used for"
                className={cn(colors.inputBg, colors.inputBorder)}
              />
            </div>
            <div>
              <label className={cn("text-sm font-medium mb-2 block", colors.textSecondary)}>
                Scopes
              </label>
              <div className="flex flex-wrap gap-2">
                {availableScopes.map(scope => (
                  <button
                    key={scope}
                    onClick={() => toggleScope(scope)}
                    className={cn(
                      "px-3 py-1.5 rounded-full text-sm font-medium border transition-colors",
                      formScopes.includes(scope)
                        ? "bg-purple-600 text-white border-purple-600"
                        : cn("border-white/10 hover:border-white/20", colors.textMuted)
                    )}
                  >
                    {SCOPE_LABELS[scope] || scope}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex items-center gap-2 pt-2">
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Key className="w-4 h-4 mr-2" />}
                Create Key
              </Button>
              <Button variant="ghost" onClick={() => { setShowForm(false); setFormScopes([]); }}>
                Cancel
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        <div className="flex justify-end">
          <Button onClick={() => setShowForm(true)}>
            <Plus className="w-4 h-4 mr-2" />
            Create API Key
          </Button>
        </div>
      )}

      {/* Key list */}
      {keys.length === 0 && !showForm ? (
        <Card className={cn(colors.cardBg, colors.cardBorder)}>
          <CardContent className="py-12 text-center">
            <Key className={cn("w-10 h-10 mx-auto mb-3", colors.textMuted)} />
            <p className={cn("text-sm", colors.textMuted)}>
              No API keys yet. Create one to get started.
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {keys.map(key => (
            <Card key={key.key_id} className={cn(colors.cardBg, colors.cardBorder)}>
              <CardContent className="py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={cn("font-medium text-sm", colors.textPrimary)}>
                        {key.name}
                      </span>
                      {key.scopes?.map(scope => (
                        <Badge key={scope} variant="secondary" className="text-xs">
                          {SCOPE_LABELS[scope] || scope}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-1">
                      <code className={cn("text-xs font-mono", colors.textMuted)}>
                        {key.key_preview}
                      </code>
                      {key.description && (
                        <span className={cn("text-xs", colors.textMuted)}>
                          {key.description}
                        </span>
                      )}
                    </div>
                    <div className={cn("text-xs mt-1", colors.textMuted)}>
                      Created {new Date(key.created_at).toLocaleDateString()}
                      {key.last_used_at && (
                        <> · Last used {new Date(key.last_used_at).toLocaleDateString()}</>
                      )}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleRevoke(key.key_id, key.name)}
                    disabled={revoking === key.key_id}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                  >
                    {revoking === key.key_id
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Trash2 className="w-4 h-4" />
                    }
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ScopedApiKeysTab;
